// client/src/hooks/useRelationships.js - VERSÃO 100% COMPLETA E CORRIGIDA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

export const ACCOUNT_USERS_QUERY_KEY = 'accountUsers';
export const ACCOUNT_CLIENTES_QUERY_KEY = 'accountClientes';
export const USER_SERVICOS_QUERY_KEY = 'userServicos';
export const USER_UNIDADES_QUERY_KEY = 'userUnidades';
export const USER_FRANQUIAS_QUERY_KEY = 'userFranquias';

const handleQueryError = (error, defaultMessage, setAlert, showAlert = true) => {
  console.error(`❌ ${defaultMessage}:`, error);
  if (showAlert && ![404, 400, 500].includes(error.response?.status)) {
    setAlert(defaultMessage, 'danger');
  }
  return { success: false, data: [] };
};

const processApiResponse = (response) => {
  if (!response) return { success: false, data: [] };
  if (response.data?.success !== undefined && response.data?.data !== undefined) {
    return response.data;
  }
  if (Array.isArray(response.data)) {
    return { success: true, data: response.data };
  }
  return { success: true, data: response.data?.data || response.data || [] };
};

// =============================================
// ✅ HOOKS PARA USER-UNIDADES (Profissional/Comissões)
// =============================================

export const useUserUnidades = (params = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = params;

  return useQuery({
    queryKey: [USER_UNIDADES_QUERY_KEY, params],
    queryFn: async () => {
      try {
        if (params.account) {
            try {
                const res = await api.get(`/relationships/accounts/${params.account}/user-unidades`);
                return processApiResponse(res);
            } catch (e) {
                console.log('⚠️ [1/2] Rota pública falhou, tentando fallback genérico...');
            }
        }
        
        const response = await api.get('/relationships/user-unidades', { params });
        return processApiResponse(response);
      } catch (error) {
        if ([404].includes(error.response?.status)) {
            return { success: true, data: [] }; 
        }
        return handleQueryError(error, 'Erro ao carregar vínculos de unidades', setAlert, true);
      }
    },
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if ([404, 400, 500].includes(error.response?.status)) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

export const useUserUnidadesByUser = (userId, accountId = null, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_UNIDADES_QUERY_KEY, 'user', userId, accountId],
    queryFn: async () => {
      if (!userId) return { success: true, data: [] };
      
      try {
        console.log('🔄 [1/2] Tentando rota /relationships/user-unidades');
        const params = { user: userId };
        if (accountId) params.account = accountId;
        
        const response = await api.get('/relationships/user-unidades', { params });
        const result = processApiResponse(response);
        
        if (response.status === 200) {
            console.log(`✅ [1/2] Rota /relationships/user-unidades retornou ${result.data?.length || 0} unidades`);
            
            const dataComIds = (result.data || []).map(item => {
              if (!item) return item;
              if (!item._id && item.id) item._id = item.id;
              if (!item.id && item._id) item.id = item._id;
              return item;
            });
            
            return { ...result, data: dataComIds, from: 'relationships' };
        }
      } catch (error) {
        console.log('⚠️ [1/2] Rota /relationships/user-unidades falhou:', error.message);
      }
      
      try {
        console.log('🔄 [2/2] Tentando rota /vinculos (fallback)');
        const response = await api.get(`/users/${userId}/vinculos`);
        const data = response.data?.data || {};
        
        let unidades = [];
        
        if (data.unidades && Array.isArray(data.unidades)) {
          unidades = data.unidades;
        } else if (data.userUnidades && Array.isArray(data.userUnidades)) {
          unidades = data.userUnidades;
        } else if (data.vinculos && Array.isArray(data.vinculos)) {
          unidades = data.vinculos
            .filter(v => v.tipo === 'unidade' || v.unidade)
            .map(v => v.unidade || v);
        }
        
        unidades = unidades.filter(u => u.status === 'ativo');
        
        unidades = unidades.map(item => {
          if (!item) return item;
          if (!item._id && item.id) item._id = item.id;
          if (!item.id && item._id) item.id = item._id;
          return item;
        });
        
        console.log(`✅ [2/2] Rota /vinculos retornou ${unidades.length} unidades`);
        return { success: true, data: unidades, from: 'vinculos' };
      } catch (error) {
        console.log('⚠️ [2/2] Rota /vinculos falhou:', error.message);
        return handleQueryError(error, 'Erro ao carregar vínculos de unidades', setAlert, false);
      }
    },
    enabled: !!userId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if ([404, 400, 500].includes(error.response?.status)) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

export const useUserUnidadesByUnidade = (unidadeId, accountId = null, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, status, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_UNIDADES_QUERY_KEY, 'unidade', unidadeId, accountId, status],
    queryFn: async () => {
      if (!unidadeId) return { success: true, data: [] };
      
      try {
        const params = { unidade: unidadeId };
        if (accountId) params.account = accountId;
        if (status && status !== '') params.status = status;
        
        console.log(`🔄 Buscando vínculos da unidade ${unidadeId}...`);
        const response = await api.get('/relationships/user-unidades', { params });
        const result = processApiResponse(response);
        
        const dataComIds = (result.data || []).map(item => {
          if (!item) return item;
          if (!item._id && item.id) item._id = item.id;
          if (!item.id && item._id) item.id = item._id;
          return item;
        });
        
        console.log(`✅ Encontrados ${dataComIds.length} vínculos`);
        return { ...result, data: dataComIds };
      } catch (error) {
        console.error(`❌ Erro ao carregar vínculos:`, error);
        return handleQueryError(error, 'Erro ao carregar vínculos da unidade', setAlert, false);
      }
    },
    enabled: !!unidadeId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useProfissionaisByUnidade = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, status, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_UNIDADES_QUERY_KEY, 'profissionais', unidadeId, status],
    queryFn: async () => {
      if (!unidadeId) return { success: true, data: [] };
      
      try {
        const queryParams = new URLSearchParams();
        if (status && status !== '') queryParams.append('status', status);
        
        const url = `/unidades/${unidadeId}/profissionais${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await api.get(url);
        const result = processApiResponse(response);
        
        const dataComIds = (result.data || []).map(item => {
          if (!item) return item;
          if (!item._id && item.id) item._id = item.id;
          if (!item.id && item._id) item.id = item._id;
          return item;
        });
        
        return { ...result, data: dataComIds };
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar profissionais da unidade', setAlert, false);
      }
    },
    enabled: !!unidadeId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

// =============================================
// ✅ HOOKS PARA USER-SERVICOS (VERSÃO OTIMIZADA)
// =============================================

export const useUserServicos = (params = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = params;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const response = await api.get('/relationships/user-servicos', { params });
        return processApiResponse(response);
      } catch (error) {
        if (error.response?.status === 404) {
            return { success: true, data: [] };
        }
        // Se der 500 no back (como estava acontecendo), vai estourar pro dev arrumar, ou alertar na UI
        console.error("❌ Erro em /relationships/user-servicos:", error);
        return handleQueryError(error, 'Erro ao carregar relacionamentos de serviços', setAlert, false);
      }
    },
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if ([404, 400, 500].includes(error.response?.status)) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

export const useUserServicosByUser = (userId, accountId = null, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, 'user', userId, accountId],
    queryFn: async () => {
      if (!userId) return { success: true, data: [] };
      
      try {
        console.log('🔄 [1/2] Tentando rota /relationships/users/servicos');
        const params = {};
        if (accountId) params.accountId = accountId;
        
        const response = await api.get(`/relationships/users/${userId}/servicos`, { params });
        const result = processApiResponse(response);
        
        if (response.status === 200) {
            console.log(`✅ [1/2] Rota /relationships/users/servicos retornou ${result.data?.length || 0} serviços`);
            return { ...result, from: 'relationships' };
        }
      } catch (error) {
        console.log('⚠️ [1/2] Rota /relationships/users/servicos falhou:', error.message);
      }
      
      try {
        console.log('🔄 [2/2] Tentando rota /vinculos');
        const response = await api.get(`/users/${userId}/vinculos`);
        const data = response.data?.data || {};
        
        let servicos = [];
        
        if (data.servicos && Array.isArray(data.servicos)) {
          servicos = data.servicos;
        } else if (data.userServicos && Array.isArray(data.userServicos)) {
          servicos = data.userServicos;
        }
        
        servicos = servicos.filter(s => s.status === 'ativo');
        
        console.log(`✅ [2/2] Rota /vinculos retornou ${servicos.length} serviços`);
        return { success: true, data: servicos, from: 'vinculos' };
      } catch (error) {
        console.log('⚠️ [2/2] Rota /vinculos falhou:', error.message);
        return handleQueryError(error, 'Erro ao carregar vínculos de serviços', setAlert, false);
      }
    },
    enabled: !!userId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if ([404, 400, 500].includes(error.response?.status)) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

export const useUserServicosByServico = (servicoId, accountId = null, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, status, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, 'servico', servicoId, accountId, status],
    queryFn: async () => {
      if (!servicoId) return { success: true, data: [] };
      
      try {
        const params = {};
        if (accountId) params.accountId = accountId;
        if (status && status !== '') params.status = status;
        
        const response = await api.get(`/relationships/servicos/${servicoId}/users`, { params });
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar profissionais do serviço', setAlert, false);
      }
    },
    enabled: !!servicoId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useProfissionaisByServicoAndUnidade = (servicoId, unidadeId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, 'profissionais', servicoId, unidadeId],
    queryFn: async () => {
      if (!servicoId || !unidadeId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/relationships/servicos/${servicoId}/unidades/${unidadeId}/profissionais`);
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar profissionais disponíveis', setAlert, false);
      }
    },
    enabled: !!servicoId && !!unidadeId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

// =============================================
// ✅ HOOKS PARA ACCOUNT-USERS
// =============================================

export const useAccountUsers = (params = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = params;

  return useQuery({
    queryKey: [ACCOUNT_USERS_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const response = await api.get('/relationships/account-users', { params });
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar vínculos de usuários', setAlert, true);
      }
    },
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useAccountUsersByAccount = (accountId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, status, ...queryOptions } = options;

  return useQuery({
    queryKey: [ACCOUNT_USERS_QUERY_KEY, 'account', accountId, status],
    queryFn: async () => {
      if (!accountId) return { success: true, data: [] };
      
      try {
        const queryParams = new URLSearchParams();
        if (status && status !== '') queryParams.append('status', status);
        
        const url = `/contas/${accountId}/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await api.get(url);
        return processApiResponse(response);
      } catch (mainError) {
        if (mainError.response?.status === 404 || mainError.response?.status === 403) {
          try {
            const fallbackResponse = await api.get(`/relationships/accounts/${accountId}/users`);
            return processApiResponse(fallbackResponse);
          } catch (fallbackError) {
            return handleQueryError(fallbackError, 'Erro ao carregar usuários da conta', setAlert, true);
          }
        }
        return handleQueryError(mainError, 'Erro ao carregar usuários da conta', setAlert, true);
      }
    },
    enabled: !!accountId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useAccountUsersByUser = (userId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [ACCOUNT_USERS_QUERY_KEY, 'user', userId],
    queryFn: async () => {
      if (!userId) return { success: true, data: [] };
      
      try {
        console.log(`🔄 Buscando AccountUser direto de: /relationships/account-users?user=${userId}`);
        const response = await api.get('/relationships/account-users', { params: { user: userId } });
        const result = processApiResponse(response);
        
        if (response.status === 200) {
            if (result.data && result.data.length > 0) {
              const dataComIds = result.data.map(item => ({
                ...item,
                _id: item._id || item.id,
                id: item.id || item._id
              }));
              console.log(`✅ Sucesso! Encontrados ${dataComIds.length} vínculos de conta.`);
              return { success: true, data: dataComIds };
            }
            return { success: true, data: [] }; 
        }
      } catch (error) {
        console.log('⚠️ Falha na rota direta, tentando fallback...');
      }

      try {
        const response = await api.get(`/users/${userId}/vinculos`);
        const data = response.data?.data || {};
        
        let vinculos = [];
        
        if (data.vinculosAccount && Array.isArray(data.vinculosAccount)) {
          vinculos = data.vinculosAccount;
        } else if (data.accounts && Array.isArray(data.accounts)) {
          vinculos = data.accounts;
        } else if (data.accountUsers && Array.isArray(data.accountUsers)) {
          vinculos = data.accountUsers;
        } else if (data.vinculos && Array.isArray(data.vinculos)) {
          vinculos = data.vinculos.filter(v => v.tipo === 'account' || v.account);
        }
        
        vinculos = vinculos.map(item => ({
          ...item,
          _id: item._id || item.id,
          id: item.id || item._id
        }));
        
        return { success: true, data: vinculos };
      } catch (error) {
        console.error('❌ Erro ao carregar vínculos do usuário:', error);
        return { success: true, data: [] };
      }
    },
    enabled: !!userId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if ([404, 400, 500].includes(error.response?.status)) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

// =============================================
// ✅ NOVOS HOOKS PARA DESVINCULAÇÃO - ACCOUNT USER
// =============================================

export const useVerificarAgendamentosFuturosAccountUser = () => {
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ userId, accountId }) => {
      if (!userId || !accountId) {
        throw new Error('Usuário e conta são obrigatórios');
      }

      const response = await api.get(
        `/relationships/account-users/usuario/${userId}/conta/${accountId}/agendamentos-futuros`
      );
      return response.data;
    },
    onError: (error) => {
      console.error('❌ Erro ao verificar agendamentos futuros:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao verificar agendamentos';
      setAlert(errorMsg, 'danger');
    }
  });
};

export const useDesvincularUsuarioDaConta = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ userId, accountId, substitutoId, motivo }) => {
      if (!userId || !accountId) {
        throw new Error('Usuário e conta são obrigatórios');
      }

      const response = await api.post(
        `/relationships/account-users/usuario/${userId}/conta/${accountId}/desvincular`,
        { substitutoId, motivo }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
      
      const message = data.data?.tipo === 'desvinculacao_simples'
        ? 'Usuário desvinculado da conta com sucesso!'
        : 'Usuário desvinculado da conta com sucesso! Agendamentos transferidos.';
      
      setAlert(message, 'success');
    },
    onError: (error) => {
      console.error('❌ Erro ao desvincular usuário da conta:', error);
      
      if (error.response?.status === 409) {
        return error.response.data;
      }
      
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao desvincular usuário';
      setAlert(errorMsg, 'danger');
      throw error;
    }
  });
};

export const useRemoverUnidadeDoUsuario = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ userId, unidadeId, substitutoId, motivo }) => {
      if (!userId || !unidadeId) {
        throw new Error('Usuário e unidade são obrigatórios');
      }

      const response = await api.post(
        `/relationships/account-users/usuario/${userId}/unidade/${unidadeId}/remover`,
        { substitutoId, motivo }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
      
      const message = data.data?.tipo === 'remocao_simples'
        ? 'Unidade removida do usuário com sucesso!'
        : 'Unidade removida do usuário com sucesso! Agendamentos transferidos.';
      
      setAlert(message, 'success');
    },
    onError: (error) => {
      console.error('❌ Erro ao remover unidade do usuário:', error);
      
      if (error.response?.status === 409) {
        return error.response.data;
      }
      
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao remover unidade';
      setAlert(errorMsg, 'danger');
      throw error;
    }
  });
};

// =============================================
// ✅ NOVOS HOOKS PARA DESVINCULAÇÃO - USER UNIDADE
// =============================================

export const useVerificarAgendamentosUserUnidade = (relationshipId, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [USER_UNIDADES_QUERY_KEY, 'agendamentos', relationshipId],
    queryFn: async () => {
      if (!relationshipId) return { success: true, data: { temAgendamentos: false, quantidade: 0, agendamentos: [] } };
      
      try {
        const response = await api.get(`/relationships/user-unidades/${relationshipId}/agendamentos-futuros`);
        return response.data;
      } catch (error) {
        console.error(`❌ Erro ao verificar agendamentos da unidade:`, error);
        return { 
          success: false, 
          data: { temAgendamentos: false, quantidade: 0, agendamentos: [] },
          error: error.message 
        };
      }
    },
    enabled: !!relationshipId && enabled,
    staleTime: 1 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status === 404 || error.response?.status === 400) return false;
      return failureCount < 2;
    }
  });
};

export const useDesvincularUserUnidade = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ relationshipId, substitutoId, motivo }) => {
      if (!relationshipId) {
        throw new Error('ID da relação é obrigatório');
      }

      const response = await api.post(`/relationships/user-unidades/${relationshipId}/desvincular`, {
          substitutoId, motivo
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [USER_UNIDADES_QUERY_KEY] });
      
      const message = data.data?.tipo === 'desvinculacao_simples'
        ? 'Usuário desvinculado da unidade com sucesso!'
        : 'Usuário desvinculado da unidade com sucesso! Agendamentos transferidos.';
      
      setAlert(message, 'success');
    },
    onError: (error) => {
      console.error('❌ Erro ao desvincular usuário da unidade:', error);
      
      if (error.response?.status === 409) {
        return error.response.data;
      }
      
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao desvincular usuário';
      setAlert(errorMsg, 'danger');
      throw error;
    }
  });
};

// =============================================
// ✅ NOVOS HOOKS PARA DESVINCULAÇÃO - USER SERVIÇO
// =============================================

export const useVerificarAgendamentosUserServico = (relationshipId, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, 'agendamentos', relationshipId],
    queryFn: async () => {
      if (!relationshipId) return { success: true, data: { temAgendamentos: false, quantidade: 0, agendamentos: [] } };
      
      try {
        const response = await api.get(`/relationships/user-servicos/${relationshipId}/agendamentos-futuros`);
        return response.data;
      } catch (error) {
        console.error(`❌ Erro ao verificar agendamentos do serviço:`, error);
        return { 
          success: false, 
          data: { temAgendamentos: false, quantidade: 0, agendamentos: [] },
          error: error.message 
        };
      }
    },
    enabled: !!relationshipId && enabled,
    staleTime: 1 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status === 404 || error.response?.status === 400) return false;
      return failureCount < 2;
    }
  });
};

export const useVerificarSubstitutoServico = (relationshipId, substitutoId, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, 'verificar-substituto', relationshipId, substitutoId],
    queryFn: async () => {
      if (!relationshipId || !substitutoId) {
        return { success: true, data: { valido: false, motivo: 'IDs não fornecidos' } };
      }
      
      try {
        const response = await api.get(
          `/relationships/user-servicos/${relationshipId}/verificar-substituto/${substitutoId}`
        );
        return response.data;
      } catch (error) {
        console.error(`❌ Erro ao verificar substituto:`, error);
        return { 
          success: false, 
          data: { valido: false, motivo: 'Erro na verificação' },
          error: error.message 
        };
      }
    },
    enabled: !!relationshipId && !!substitutoId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status === 404 || error.response?.status === 400) return false;
      return failureCount < 2;
    }
  });
};

export const useDesvincularUserServico = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ relationshipId, substitutoId, motivo }) => {
      if (!relationshipId) {
        throw new Error('ID da relação é obrigatório');
      }

      const response = await api.post(`/relationships/user-servicos/${relationshipId}/desvincular`, {
          substitutoId, motivo
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
      
      const message = data.data?.tipo === 'desvinculacao_simples'
        ? 'Profissional desvinculado do serviço com sucesso!'
        : 'Profissional desvinculado do serviço com sucesso! Agendamentos transferidos.';
      
      setAlert(message, 'success');
    },
    onError: (error) => {
      console.error('❌ Erro ao desvincular profissional do serviço:', error);
      
      if (error.response?.status === 409) {
        return error.response.data;
      }
      
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao desvincular profissional';
      setAlert(errorMsg, 'danger');
      throw error;
    }
  });
};

// =============================================
// ✅ HOOKS PARA USER FRANQUIA
// =============================================

export const useUserFranquias = (params = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = params;

  return useQuery({
    queryKey: [USER_FRANQUIAS_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const response = await api.get('/relationships/user-franquias', { params });
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar vínculos de franquias', setAlert, true);
      }
    },
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useUserFranquiasByUser = (userId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_FRANQUIAS_QUERY_KEY, 'user', userId],
    queryFn: async () => {
      if (!userId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/relationships/users/${userId}/franquias`);
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, `Erro ao carregar franquias do usuário`, setAlert, false);
      }
    },
    enabled: !!userId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useUserFranquiasByFranquia = (franquiaId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_FRANQUIAS_QUERY_KEY, 'franquia', franquiaId],
    queryFn: async () => {
      if (!franquiaId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/relationships/franquias/${franquiaId}/users`);
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, `Erro ao carregar usuários da franquia`, setAlert, false);
      }
    },
    enabled: !!franquiaId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useUsersByFranquiaAndRole = (franquiaId, roleId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_FRANQUIAS_QUERY_KEY, 'franquia-role', franquiaId, roleId],
    queryFn: async () => {
      if (!franquiaId || !roleId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/relationships/franquias/${franquiaId}/roles/${roleId}/users`);
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, `Erro ao carregar usuários por role`, setAlert, false);
      }
    },
    enabled: !!franquiaId && !!roleId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useVerificarAcessoFranquia = (userId, franquiaId, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [USER_FRANQUIAS_QUERY_KEY, 'acesso', userId, franquiaId],
    queryFn: async () => {
      if (!userId || !franquiaId) return { success: true, data: { temAcesso: false } };
      
      try {
        const response = await api.get(`/relationships/users/${userId}/franquias/${franquiaId}/acesso`);
        return processApiResponse(response);
      } catch (error) {
        console.error(`❌ Erro ao verificar acesso à franquia:`, error);
        return { success: false, data: { temAcesso: false, erro: error.message } };
      }
    },
    enabled: !!userId && !!franquiaId && enabled,
    staleTime: 5 * 60 * 1000,
    ...queryOptions
  });
};

// =============================================
// ✅ HOOKS PARA FRANQUIA
// =============================================

export const useFranquiaAdmins = (franquiaId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, silent = false, ...queryOptions } = options;

  return useQuery({
    queryKey: ['franquiaAdmins', franquiaId],
    queryFn: async () => {
      if (!franquiaId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/franquias/${franquiaId}/users`);
        const result = processApiResponse(response);
        
        if (!result.success || !Array.isArray(result.data)) {
          return { success: true, data: [] };
        }
        
        const admins = result.data
          .filter(vinc => {
            const user = vinc.user || {};
            const role = vinc.role || {};
            const roleName = typeof role === 'object' ? role.name : role;
            
            return (
              vinc.status === 'ativo' &&
              (roleName === 'franquia_admin' || roleName === 'admin') &&
              user.isActive !== false
            );
          })
          .map(vinc => ({
            ...(vinc.user || {}),
            _id: vinc.user?._id || vinc._id,
            relationshipId: vinc._id,
            role: vinc.role,
            roleName: typeof vinc.role === 'object' ? vinc.role.name : vinc.role,
            status: vinc.status,
            vinculoData: vinc
          }));
        
        return {
          success: true,
          data: admins,
          count: admins.length
        };
        
      } catch (error) {
        console.error(`❌ Erro ao buscar administradores da franquia:`, error);
        
        if (!silent && error.response?.status !== 404) {
          setAlert('Erro ao carregar administradores da franquia', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          error: error.response?.data?.error || error.message
        };
      }
    },
    enabled: !!franquiaId && enabled,
    staleTime: 5 * 60 * 1000,
    ...queryOptions
  });
};

export const useFranquiaUsers = (franquiaId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, silent = false, ...queryOptions } = options;

  return useQuery({
    queryKey: ['franquiaUsers', franquiaId],
    queryFn: async () => {
      if (!franquiaId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/franquias/${franquiaId}/users`);
        return processApiResponse(response);
      } catch (error) {
        console.error(`❌ Erro ao buscar usuários da franquia:`, error);
        
        if (!silent && error.response?.status !== 404) {
          setAlert('Erro ao carregar usuários da franquia', 'danger');
        }
        
        return {
          success: false,
          data: [],
          error: error.response?.data?.error || error.message
        };
      }
    },
    enabled: !!franquiaId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

// =============================================
// ✅ HOOKS PARA ACCOUNT-CLIENTES
// =============================================

export const useAccountClientes = (params = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = params;

  return useQuery({
    queryKey: [ACCOUNT_CLIENTES_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const response = await api.get('/relationships/account-clientes', { params });
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar vínculos de clientes', setAlert, true);
      }
    },
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

export const useAccountClientesByAccount = (accountId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [ACCOUNT_CLIENTES_QUERY_KEY, 'account', accountId],
    queryFn: async () => {
      if (!accountId) return { success: true, data: [] };
      
      try {
        console.log(`🔄 Buscando clientes da account: ${accountId}`);
        const response = await api.get(`/relationships/accounts/${accountId}/clientes`);
        
        console.log('✅ Resposta da API de clientes:', response.data);
        
        const result = processApiResponse(response);
        
        // ✅ Log detalhado para debug
        if (result.data && result.data.length > 0) {
          console.log(`📊 Encontrados ${result.data.length} clientes`);
          result.data.forEach((ac, idx) => {
            console.log(`  Cliente ${idx + 1}:`, {
              id: ac._id,
              hasCliente: !!ac.cliente,
              clienteId: ac.cliente?._id,
              clienteNome: ac.cliente?.name || ac.cliente?.user?.name,
              clienteEmail: ac.cliente?.email || ac.cliente?.user?.email
            });
          });
        } else {
          console.log('⚠️ Nenhum cliente encontrado para esta conta');
        }
        
        return result;
      } catch (error) {
        console.error('❌ Erro ao carregar clientes da conta:', error);
        return handleQueryError(error, 'Erro ao carregar clientes da conta', setAlert, true);
      }
    },
    enabled: !!accountId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status === 404 || error.response?.status === 400) return false;
      return failureCount < 2;
    },
    ...queryOptions
  });
};

export const useAccountClientesByCliente = (clienteId, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [ACCOUNT_CLIENTES_QUERY_KEY, 'cliente', clienteId],
    queryFn: async () => {
      if (!clienteId) return { success: true, data: [] };
      
      try {
        const response = await api.get(`/relationships/clientes/${clienteId}/accounts`);
        return processApiResponse(response);
      } catch (error) {
        return handleQueryError(error, 'Erro ao carregar contas do cliente', setAlert, true);
      }
    },
    enabled: !!clienteId && enabled,
    staleTime: 2 * 60 * 1000,
    ...queryOptions
  });
};

// =============================================
// ✅ HOOKS PARA COMISSÕES
// =============================================

export const useCalcularComissaoServico = () => {
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ userServicoId, valorServico, valorTotal = null }) => {
      if (!userServicoId || !valorServico) {
        throw new Error('ID do vínculo e valor do serviço são obrigatórios');
      }

      const response = await api.post(`/relationships/user-servicos/${userServicoId}/calcular-comissao`, {
        valorServico,
        valorTotal
      });
      return response.data;
    },
    onError: (error) => {
      console.error('❌ Erro ao calcular comissão do serviço:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao calcular comissão';
      setAlert(errorMsg, 'danger');
    }
  });
};

export const useCalcularComissaoUnidade = () => {
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: async ({ userUnidadeId, valorServico, valorTotal = null }) => {
      if (!userUnidadeId || !valorServico) {
        throw new Error('ID do vínculo e valor do serviço são obrigatórios');
      }

      const response = await api.post(`/relationships/user-unidades/${userUnidadeId}/calcular-comissao`, {
        valorServico,
        valorTotal
      });
      return response.data;
    },
    onError: (error) => {
      console.error('❌ Erro ao calcular comissão da unidade:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao calcular comissão';
      setAlert(errorMsg, 'danger');
    }
  });
};

export const useCalcularComissaoAutomatica = () => {
  const { setAlert } = useAlert();
  const calcularComissaoServico = useCalcularComissaoServico();
  const calcularComissaoUnidade = useCalcularComissaoUnidade();

  const calcular = async ({ 
    userServicoId = null, 
    userUnidadeId = null, 
    valorServico, 
    valorTotal = null 
  }) => {
    if (!valorServico) {
      throw new Error('Valor do serviço é obrigatório');
    }

    if (userServicoId) {
      try {
        const result = await calcularComissaoServico.mutateAsync({
          userServicoId,
          valorServico,
          valorTotal
        });
        return result;
      } catch (error) {
        console.warn('⚠️ Comissão por serviço falhou, tentando por unidade...');
      }
    }

    if (userUnidadeId) {
      try {
        const result = await calcularComissaoUnidade.mutateAsync({
          userUnidadeId,
          valorServico,
          valorTotal
        });
        return result;
      } catch (error) {
        console.warn('⚠️ Comissão por unidade também falhou');
      }
    }

    return {
      success: true,
      data: {
        comissaoCalculada: 0,
        configuracaoUsada: 'nenhuma',
        mensagem: 'Nenhuma comissão configurada'
      }
    };
  };

  return {
    calcular,
    isLoading: calcularComissaoServico.isPending || calcularComissaoUnidade.isPending,
    isError: calcularComissaoServico.isError || calcularComissaoUnidade.isError,
    error: calcularComissaoServico.error || calcularComissaoUnidade.error
  };
};

// =============================================
// ✅ MUTATIONS PARA GERENCIAR RELACIONAMENTOS
// =============================================

export const useRelationshipsMutations = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateRelationships = () => {
    queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [ACCOUNT_CLIENTES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_UNIDADES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_FRANQUIAS_QUERY_KEY] });
  };

  const handleSuccess = (message) => {
    invalidateRelationships();
    setAlert(message, 'success');
  };

  const handleError = (error, defaultMessage) => {
    console.error('❌ Erro na mutation de relacionamento:', error);
    
    if (error.response?.status === 404) {
      setAlert('Esta funcionalidade ainda não está disponível no servidor. Entre em contato com o suporte.', 'warning');
      return;
    }
    
    const errorMsg = error.response?.data?.error || error.message || defaultMessage;
    setAlert(errorMsg, 'danger');
  };

  // UserFranquia Mutations
  const createUserFranquia = useMutation({
    mutationFn: (data) => api.post('/relationships/user-franquias', data),
    onSuccess: () => handleSuccess('Vínculo usuário-franquia criado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao criar vínculo usuário-franquia')
  });

  const updateUserFranquia = useMutation({
    mutationFn: ({ id, data }) => api.put(`/relationships/user-franquias/${id}`, data),
    onSuccess: () => handleSuccess('Vínculo usuário-franquia atualizado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo usuário-franquia')
  });

  const deleteUserFranquia = useMutation({
    mutationFn: (id) => api.delete(`/relationships/user-franquias/${id}`),
    onSuccess: () => handleSuccess('Vínculo usuário-franquia removido com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao remover vínculo usuário-franquia')
  });

  const vincularUsuarioFranquia = useMutation({
    mutationFn: ({ userId, data }) => api.post(`/relationships/users/${userId}/vincular-franquia`, data),
    onSuccess: () => handleSuccess('Usuário vinculado à franquia com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao vincular usuário à franquia')
  });

  // AccountUser Mutations
  const createAccountUser = useMutation({
    mutationFn: (data) => api.post('/relationships/account-users', data),
    onSuccess: () => handleSuccess('Vínculo usuário-conta criado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao criar vínculo usuário-conta')
  });

  const updateAccountUser = useMutation({
    mutationFn: ({ id, data }) => api.put(`/relationships/account-users/${id}`, data),
    onSuccess: () => handleSuccess('Vínculo usuário-conta atualizado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo usuário-conta')
  });

  const deleteAccountUser = useMutation({
    mutationFn: (id) => api.delete(`/relationships/account-users/${id}`),
    onSuccess: () => handleSuccess('Vínculo usuário-conta removido com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao remover vínculo usuário-conta')
  });

  // AccountCliente Mutations
  const createAccountCliente = useMutation({
    mutationFn: (data) => api.post('/relationships/account-clientes', data),
    onSuccess: () => handleSuccess('Vínculo cliente-conta criado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao criar vínculo cliente-conta')
  });

  const updateAccountCliente = useMutation({
    mutationFn: ({ id, data }) => api.put(`/relationships/account-clientes/${id}`, data),
    onSuccess: () => handleSuccess('Vínculo cliente-conta atualizado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo cliente-conta')
  });

  const deleteAccountCliente = useMutation({
    mutationFn: (id) => api.delete(`/relationships/account-clientes/${id}`),
    onSuccess: () => handleSuccess('Vínculo cliente-conta removido com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao remover vínculo cliente-conta')
  });

  // UserServico Mutations
  const createUserServico = useMutation({
    mutationFn: (data) => api.post('/relationships/user-servicos', data),
    onSuccess: () => handleSuccess('Vínculo profissional-serviço criado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao criar vínculo profissional-serviço')
  });

  const updateUserServico = useMutation({
    mutationFn: ({ id, data }) => api.put(`/relationships/user-servicos/${id}`, data),
    onSuccess: () => handleSuccess('Vínculo profissional-serviço atualizado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo profissional-serviço')
  });

  const deleteUserServico = useMutation({
    mutationFn: (id) => api.delete(`/relationships/user-servicos/${id}`),
    onSuccess: () => handleSuccess('Vínculo profissional-serviço removido com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao remover vínculo profissional-serviço')
  });

  // UserUnidade Mutations
  const createUserUnidade = useMutation({
    mutationFn: (data) => api.post('/relationships/user-unidades', data),
    onSuccess: () => handleSuccess('Vínculo usuário-unidade criado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao criar vínculo usuário-unidade')
  });

  const updateUserUnidade = useMutation({
    mutationFn: ({ id, data }) => api.put(`/relationships/user-unidades/${id}`, data),
    onSuccess: () => handleSuccess('Vínculo usuário-unidade atualizado com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo usuário-unidade')
  });

  const deleteUserUnidade = useMutation({
    mutationFn: (id) => api.delete(`/relationships/user-unidades/${id}`),
    onSuccess: () => handleSuccess('Vínculo usuário-unidade removido com sucesso!'),
    onError: (error) => handleError(error, 'Erro ao remover vínculo usuário-unidade')
  });

  const verificarAgendamentosAccountUser = useVerificarAgendamentosFuturosAccountUser();
  const desvincularUsuarioDaConta = useDesvincularUsuarioDaConta();
  const removerUnidadeDoUsuario = useRemoverUnidadeDoUsuario();
  const desvincularUserUnidade = useDesvincularUserUnidade();
  const desvincularUserServico = useDesvincularUserServico();

  const calcularComissaoServico = useCalcularComissaoServico();
  const calcularComissaoUnidade = useCalcularComissaoUnidade();
  const calcularComissaoAutomatica = useCalcularComissaoAutomatica();

  return {
    createUserFranquia, updateUserFranquia, deleteUserFranquia, vincularUsuarioFranquia,
    createAccountUser, updateAccountUser, deleteAccountUser,
    createAccountCliente, updateAccountCliente, deleteAccountCliente,
    createUserServico, updateUserServico, deleteUserServico,
    createUserUnidade, updateUserUnidade, deleteUserUnidade,
    verificarAgendamentosAccountUser, desvincularUsuarioDaConta, removerUnidadeDoUsuario,
    desvincularUserUnidade, desvincularUserServico,
    calcularComissaoServico, calcularComissaoUnidade, calcularComissaoAutomatica,
    invalidateRelationships
  };
};

// =============================================
// ✅ HOOKS UTILITÁRIOS (para componentes)
// =============================================

export const useUserAccounts = (userId) => {
  const { data, isLoading, error } = useAccountUsersByUser(userId);
  
  const accounts = data?.data?.map(au => ({
    ...au.account,
    relationshipsId: au._id,
    role: au.role,
    unidades: au.unidades,
    status: au.status
  })) || [];

  return {
    accounts,
    isLoading,
    error,
    hasAccounts: accounts.length > 0
  };
};

export const useAccountClientesList = (accountId) => {
  const { data, isLoading, error } = useAccountClientesByAccount(accountId);
  
  const clientes = data?.data?.map(ac => ({
    ...ac.cliente,
    relationshipsId: ac._id,
    unidades: ac.unidades,
    status: ac.status
  })) || [];

  return {
    clientes,
    isLoading,
    error,
    count: clientes.length
  };
};

export const useUserFranquiasList = (userId) => {
  const { data, isLoading, error } = useUserFranquiasByUser(userId);
  
  const franquias = data?.data?.map(uf => ({
    ...uf.franquia,
    relationshipsId: uf._id,
    role: uf.role,
    status: uf.status
  })) || [];

  return {
    franquias,
    isLoading,
    error,
    count: franquias.length
  };
};

export const useProfessionalServices = (userId, accountId = null) => {
  const { data, isLoading, error } = useUserServicosByUser(userId, accountId);
  
  const services = data?.data?.map(us => ({
    ...us.servico,
    relationshipsId: us._id,
    unidades: us.unidades,
    comissao: us.comissao,
    status: us.status
  })) || [];

  return {
    services,
    isLoading,
    error,
    count: services.length
  };
};

export const useUserUnits = (userId, accountId = null) => {
  const { data, isLoading, error, refetch } = useUserUnidadesByUser(userId, accountId);
  
  const units = data?.data?.map(uu => ({
    ...uu.unidade,
    relationshipsId: uu._id,
    role: uu.role,
    comissao: uu.comissao,
    status: uu.status
  })) || [];

  return {
    units,
    isLoading,
    error,
    count: units.length,
    refetch
  };
};

export const useUserHasUnitAccess = (userId, unidadeId, accountId = null) => {
  const { data, isLoading, error } = useUserUnidadesByUser(userId, accountId);
  
  const hasAccess = data?.data?.some(uu => {
    const unidadeObj = uu.unidade || {};
    const unidade = unidadeObj._id || unidadeObj.id || unidadeObj;
    return (unidade === unidadeId || unidade?.toString() === unidadeId?.toString()) && 
           uu.status === 'ativo';
  }) || false;

  return {
    hasAccess,
    isLoading,
    error,
    userUnidades: data?.data || []
  };
};

export const useUserHasFranquiaAccess = (userId, franquiaId) => {
  const { data, isLoading, error } = useVerificarAcessoFranquia(userId, franquiaId);
  
  const hasAccess = data?.data?.temAcesso || false;
  const vinculo = data?.data?.vinculo;

  return {
    hasAccess,
    vinculo,
    isLoading,
    error
  };
};

export const useAvailableProfessionals = (servicoId, unidadeId) => {
  const { data, isLoading, error } = useProfissionaisByServicoAndUnidade(servicoId, unidadeId);
  
  const availableProfessionals = data?.data?.map(us => ({
    ...us.user,
    relationshipsId: us._id,
    servico: us.servico,
    unidades: us.unidades,
    comissao: us.comissao,
    status: us.status
  })) || [];

  return {
    professionals: availableProfessionals,
    isLoading,
    error,
    count: availableProfessionals.length
  };
};

export const useComissaoServico = (userServicoId) => {
  const { data } = useUserServicos();
  
  const userServico = data?.data?.find(us => us._id === userServicoId);
  
  return {
    comissao: userServico?.comissao || { ativo: false, faixas: [] },
    hasComissao: userServico?.comissao?.ativo || false
  };
};

export const useComissaoUnidade = (userUnidadeId) => {
  const { data } = useUserUnidades();
  
  const userUnidade = data?.data?.find(uu => uu._id === userUnidadeId);
  
  return {
    comissao: userUnidade?.comissao || { ativo: false, faixas: [] },
    hasComissao: userUnidade?.comissao?.ativo || false
  };
};

export const useUserServicoDetails = (relationshipId) => {
  const { data, isLoading, error } = useUserServicos();
  
  const userServico = data?.data?.find(us => us._id === relationshipId);
  
  return {
    userServico,
    isLoading,
    error
  };
};

export const useUserUnidadeDetails = (relationshipId) => {
  const { data, isLoading, error } = useUserUnidades();
  
  const userUnidade = data?.data?.find(uu => uu._id === relationshipId);
  
  return {
    userUnidade,
    isLoading,
    error
  };
};